import {addMoney, compareMoney} from "../../shared/finance/decimal";

export class SplitBillContractError extends Error {
    constructor(message) {
        super(message);
        this.name = "SplitBillContractError";
        this.kind = "contract";
    }
}

export const SPLIT_GROUP_STATUSES = Object.freeze([
    "pending",
    "partially_paid",
    "paid",
    "cancelled",
    "expired",
]);

export const SPLIT_PART_STATUSES = Object.freeze([
    "pending",
    "paid",
    "cancelled",
    "expired",
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MONEY_RE = /^(?:0|[1-9]\d*)\.\d{2}$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const object = (value, label) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new SplitBillContractError(`${label} must be an object`);
    }
    return value;
};

const field = (value, key, label = key) => {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
        throw new SplitBillContractError(`${label} is required`);
    }
    return value[key];
};

const uuid = (value, label) => {
    if (typeof value !== "string" || !UUID_RE.test(value)) {
        throw new SplitBillContractError(`${label} must be a UUID`);
    }
    return value;
};

const positiveInteger = (value, label) => {
    if (!Number.isInteger(value) || value <= 0) {
        throw new SplitBillContractError(`${label} must be a positive integer`);
    }
    return value;
};

const nullablePositiveInteger = (value, label) =>
    value === null ? null : positiveInteger(value, label);

const text = (value, label, {empty = false} = {}) => {
    if (typeof value !== "string" || (!empty && !value.trim())) {
        throw new SplitBillContractError(`${label} must be text`);
    }
    return value;
};

const nullableText = (value, label) =>
    value === null ? null : text(value, label);

const enumValue = (value, allowed, label) => {
    if (!allowed.includes(value)) {
        throw new SplitBillContractError(`${label} has an unknown value`);
    }
    return value;
};

const money = (value, label) => {
    if (typeof value !== "string" || !MONEY_RE.test(value) || compareMoney(value, "0.00") <= 0) {
        throw new SplitBillContractError(`${label} must be positive canonical money`);
    }
    return value;
};

const timestamp = (value, label, {nullable = false} = {}) => {
    if (nullable && value === null) return null;
    if (typeof value !== "string" || !ISO_RE.test(value) || Number.isNaN(Date.parse(value))) {
        throw new SplitBillContractError(`${label} must be an ISO timestamp${nullable ? " or null" : ""}`);
    }
    return value;
};

const absolutePaymentUrl = (value, label) => {
    if (typeof value !== "string") throw new SplitBillContractError(`${label} must be a URL`);
    let parsed;
    try {
        parsed = new URL(value);
    } catch (_error) {
        throw new SplitBillContractError(`${label} must be an absolute URL`);
    }
    if (!["http:", "https:"].includes(parsed.protocol)) {
        throw new SplitBillContractError(`${label} must use http or https`);
    }
    return value;
};

const normalizePart = (value) => {
    const part = object(value, "split bill part");
    const payBillId = uuid(field(part, "pay_bill_id"), "pay_bill_id");
    const paymentIntentUrl = text(field(part, "payment_intent_url"), "payment_intent_url");
    if (paymentIntentUrl !== `/api/v2/client/payment-intents/${payBillId}`) {
        throw new SplitBillContractError("payment_intent_url must target its pay bill");
    }
    return {
        id: uuid(field(part, "id"), "part.id"),
        pay_bill_id: payBillId,
        part_number: positiveInteger(field(part, "part_number"), "part_number"),
        amount: money(field(part, "amount"), "part.amount"),
        status: enumValue(field(part, "status"), SPLIT_PART_STATUSES, "part.status"),
        pay_url: absolutePaymentUrl(field(part, "pay_url"), "pay_url"),
        payment_intent_url: paymentIntentUrl,
    };
};

const assertStatusConsistency = (groupStatus, parts) => {
    const statuses = parts.map(({status}) => status);
    const all = (status) => statuses.every((value) => value === status);
    const includes = (status) => statuses.includes(status);
    const valid = {
        pending: all("pending"),
        partially_paid: includes("paid") && includes("pending") && statuses.every((value) => ["paid", "pending"].includes(value)),
        paid: all("paid"),
        cancelled: all("cancelled"),
        expired: includes("expired") && statuses.every((value) => ["paid", "expired"].includes(value)),
    }[groupStatus];
    if (!valid) {
        throw new SplitBillContractError("group status must match part statuses");
    }
};

export const normalizeSplitBill = (value) => {
    const snapshot = object(value, "split bill");
    if (field(snapshot, "success") !== true) {
        throw new SplitBillContractError("success must be true");
    }
    const rawGroup = object(field(snapshot, "group"), "split bill group");
    const group = {
        id: uuid(field(rawGroup, "id"), "group.id"),
        branch_id: positiveInteger(field(rawGroup, "branch_id"), "branch_id"),
        branch_name: text(field(rawGroup, "branch_name"), "branch_name"),
        personal_id: nullablePositiveInteger(field(rawGroup, "personal_id"), "personal_id"),
        personal_name: nullableText(field(rawGroup, "personal_name"), "personal_name"),
        status: enumValue(field(rawGroup, "status"), SPLIT_GROUP_STATUSES, "group.status"),
        original_amount: money(field(rawGroup, "original_amount"), "original_amount"),
        parts_count: positiveInteger(field(rawGroup, "parts_count"), "parts_count"),
        expires_at: timestamp(field(rawGroup, "expires_at"), "expires_at", {nullable: true}),
        created_at: timestamp(field(rawGroup, "created_at"), "created_at"),
    };
    if ((group.personal_id === null) !== (group.personal_name === null)) {
        throw new SplitBillContractError("personal_id and personal_name must be nullable together");
    }

    const rawParts = field(snapshot, "parts");
    if (!Array.isArray(rawParts)) throw new SplitBillContractError("parts must be an array");
    const parts = rawParts.map(normalizePart).sort((left, right) => left.part_number - right.part_number);
    if (parts.length !== group.parts_count) {
        throw new SplitBillContractError("parts length must match parts_count");
    }
    const numbers = parts.map(({part_number: number}) => number);
    if (new Set(numbers).size !== parts.length || numbers.some((number, index) => number !== index + 1)) {
        throw new SplitBillContractError("part numbers must be unique and contiguous");
    }
    if (compareMoney(addMoney(parts.map(({amount}) => amount)), group.original_amount) !== 0) {
        throw new SplitBillContractError("part amounts must equal original_amount");
    }
    assertStatusConsistency(group.status, parts);
    return {success: true, group, parts};
};

export const normalizeSplitBillList = (value) => {
    if (!Array.isArray(value)) throw new SplitBillContractError("split bill list must be an array");
    return value.map(normalizeSplitBill);
};
